import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UserStats {
  xp: number;
  level: number;
  streak: number;
}

interface UserBadge {
  badge_slug: string;
  earned_at: string;
}

interface Quest {
  id: string;
  title: string;
  slug: string;
  xp: number;
  min_level: number;
}

interface UserQuest {
  quest_id: string;
  completed_at: string | null;
}

export const useProgressData = (userId: string | undefined) => {
  const [stats, setStats] = useState<UserStats>({ xp: 0, level: 0, streak: 0 });
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [quests, setQuests] = useState<Quest[]>([]);
  const [userQuests, setUserQuests] = useState<UserQuest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserData = useCallback(async () => {
    if (!userId) return;

    try {
      setError(null);
      
      // First fetch user XP to determine level
      const xpResponse = await supabase.from('user_xp').select('xp, level').eq('user_id', userId).maybeSingle();
      
      if (xpResponse.error) throw xpResponse.error;
      
      const userLevel = xpResponse.data?.level || 0;
      
      // Fetch other data in parallel, filtering quests by level
      const [streakResponse, badgeResponse, questResponse, userQuestResponse] = await Promise.all([
        supabase.from('user_streaks').select('current_streak').eq('user_id', userId).maybeSingle(),
        supabase.from('user_badges').select('badge_slug, earned_at').eq('user_id', userId),
        supabase.from('quests').select('*').lte('min_level', userLevel),
        supabase.from('user_quests').select('quest_id, completed_at').eq('user_id', userId)
      ]);

      // Check for errors
      if (streakResponse.error) throw streakResponse.error;
      if (badgeResponse.error) throw badgeResponse.error;
      if (questResponse.error) throw questResponse.error;
      if (userQuestResponse.error) throw userQuestResponse.error;

      setStats({
        xp: xpResponse.data?.xp || 0,
        level: xpResponse.data?.level || 0,
        streak: streakResponse.data?.current_streak || 0
      });

      setBadges(badgeResponse.data || []);
      setQuests(questResponse.data || []);
      setUserQuests(userQuestResponse.data || []);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError("Failed to load progress data");
      toast.error("Failed to load progress data");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchUserData();
    }
  }, [userId, fetchUserData]);

  // Set up real-time subscriptions for live updates
  useEffect(() => {
    if (!userId) return;

    const channels = [
      // Listen to user_xp changes
      supabase
        .channel('user_xp_changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'user_xp',
          filter: `user_id=eq.${userId}`
        }, () => {
          fetchUserData();
        }),
      
      // Listen to user_quests changes
      supabase
        .channel('user_quests_changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'user_quests',
          filter: `user_id=eq.${userId}`
        }, () => {
          fetchUserData();
        }),

      // Listen to user_badges changes
      supabase
        .channel('user_badges_changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'user_badges',
          filter: `user_id=eq.${userId}`
        }, () => {
          fetchUserData();
        }),

      // Listen to user_streaks changes
      supabase
        .channel('user_streaks_changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'user_streaks',
          filter: `user_id=eq.${userId}`
        }, () => {
          fetchUserData();
        })
    ];

    // Subscribe to all channels
    channels.forEach(channel => channel.subscribe());

    return () => {
      // Cleanup subscriptions
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [userId, fetchUserData]);

  return {
    stats,
    badges,
    quests,
    userQuests,
    loading,
    error,
    refetch: fetchUserData
  };
};